import { dehydrate, DehydratedState, hydrate } from 'react-query';
import {
  PersistedClient,
  PersistQueryClientOptions,
} from 'react-query/types/persistQueryClient-experimental';

const flatDehydrateMutation = (dehydrateData: DehydratedState): DehydratedState => {
  if ('mutations' in dehydrateData) {
    const newMutationsState: Record<
      string | number | symbol,
      typeof dehydrateData.mutations[0]
    > = {};

    dehydrateData.mutations.forEach((mutation) => {
      if (mutation.mutationKey) {
        newMutationsState[mutation.mutationKey.toString()] = mutation;
      }
    });

    dehydrateData.mutations = Object.values(newMutationsState);
  }
  return dehydrateData;
};

/**
 * Original react-query function
 * @description Added subscribe for change of mutations
 */
export async function persistQueryClient({
  queryClient,
  persistor,
  maxAge = 1000 * 60 * 60 * 24,
  buster = '',
  hydrateOptions,
  dehydrateOptions,
}: PersistQueryClientOptions) {
  if (typeof window !== 'undefined') {
    // Subscribe to changes
    const saveClient = () => {
      const persistClient: PersistedClient = {
        buster,
        timestamp: Date.now(),
        clientState: flatDehydrateMutation(dehydrate(queryClient, dehydrateOptions)),
      };

      persistor.persistClient(persistClient);
    };

    // Attempt restore
    try {
      const persistedClient = await persistor.restoreClient();

      if (persistedClient) {
        if (persistedClient.timestamp) {
          const expired = Date.now() - persistedClient.timestamp > maxAge;
          const busted = persistedClient.buster !== buster;
          if (expired || busted) {
            persistor.removeClient();
          } else {
            hydrate(queryClient, persistedClient.clientState, hydrateOptions);
          }
        } else {
          persistor.removeClient();
        }
      }
    } catch (err) {
      persistor.removeClient();
    }

    // Subscribe to changes in the query and the mutation cache to trigger the save
    queryClient.getQueryCache().subscribe(saveClient);
    queryClient.getMutationCache().subscribe(saveClient); // <- Here
  }
}
